import Seasons from './Seasons';
import { generateComponentDocs, generateStoryDocs } from '../../../utils/documentationUtils';

export default {
  title: 'Pages/Admin/Seasons',
  component: Seasons,
  parameters: {
    layout: 'fullscreen',
    ...generateComponentDocs(
      // What problem does it solve?
      `\nThe \`Seasons\` serves as a dedicated interface for managing custom time frames known as seasons.
        \n\n#### _***Key features include:***_
        \n- Create, view, and manage seasons defined by a name, start date, and end date.
        \n- Support for overlapping dates allows users to search for sites visited or not visited during broader outreach seasons or specific growing seasons.
        \n- Export and delete season records for better data organization.\n\n`,
      // When & how to use it?
      `\nThis page allows managers to organize their outreach data into logical seasons for planning, forecasting, and reporting.
        \n\n### _**Key Functionalities**_
        \n\n**Breadcrumb Navigation**:
        \n- Uses the \`Breadcrumbs\` component for navigation context.
        \n\n**Table Management**:
        \n- Displays seasons in a paginated table with sortable columns for:
        \n - **Name**\n
        \n - **Start Date**\n
        \n - **End Date**\n\n
        \n**CRUD Operations**:
        \n- **Create**: Add a new season using the \`Create a Season\` modal, accessed by clicking the \`New Season\` button.
        \n- **Read**: View season details within the table.
        \n- **Update**: Edit existing seasons inline within the table using the edit button in the \`Action\` column.
        \n- **Delete**: Remove seasons using the delete button in the \`Action\` column.
        \n\n**Export Functionality**:
        \n- Export season-specific data via the \`Export\` button in the \`Action\` column.
        \n\n**Empty State Handling**:
        \n- Displays an \`EmptyState\` component when no seasons are configured, with an option to create a new season.\n\n`,
      // Current Usage
      `\nThe \`Seasons\` is accessible to Vamos managers via the admin interface for creating and managing outreach seasons.
        \n\n### _**Access Roles**_
        \n- **Manager**: Full access to create, edit, delete, and associate seasons with sites, visits, or notes. Can also view, filter, and manage data tied to seasons across all functionalities.
        \n- **Planner**: Can create and edit seasons but cannot delete them. Can filter and view data tied to seasons, such as visits and contacts, within their organization.
        \n- **User**: Limited to viewing season-associated data via the mobile app. No access to create, edit, or delete seasons within the web app.
        \n\n>Note: The access rights described here may not have been fully implemented at the time this documentation was created. The description of roles reflects the intended behavior for access rights as outlined above.\n\n`,
      // Related Components
      `\n- **[PagesHeader](http://localhost:6006/?path=/docs/components-headers-pagesheader--docs)**: Displays the page icon and title. 
        \n- **[Breadcrumbs](http://localhost:6006/?path=/docs/components-breadcrumbs--docs)**: Provides navigation context to help users track their location within the app.
        \n- **[Table](http://localhost:6006/?path=/docs/components-table--docs)**: Displays seasons in a structured, paginated table with sortable columns.
        \n- **[Pagination](http://localhost:6006/?path=/docs/components-pagination-pagination--docs)**: Handles pagination for navigating through multiple seasons.
        \n- **[EmptyState](http://localhost:6006/?path=/docs/components-emptystate--docs)**: Displays when no seasons are available.
        \n- **[Button](http://localhost:6006/?path=/docs/components-button-button--docs)**: Used for creating new seasons.
        \n- **[ConfirmDeleteModal](http://localhost:6006/?path=/docs/components-seasons-confirmdeletemodal--docs)**: Modal for confirming season deletion.
        \n- **[EditSeason](http://localhost:6006/?path=/docs/components-seasons-editseason--docs)**: Enables inline editing of season details within the table.
        \n- **[ExportSeasonsModal](http://localhost:6006/?path=/docs/components-seasons-exportseasonsmodal--docs)**: Displays export progress and status in modal when exporting season data.
        \n- **[Modal](http://localhost:6006/?path=/docs/components-seasons-modal--docs)**: Base modal component used for displaying confirmation dialogs and forms.
        \n- **[NewSeasonModal](http://localhost:6006/?path=/docs/components-seasons-newseasonmodal--docs)**: Modal for adding a new season, including input fields for name and date range.
        \n- **[SeasonsActions](http://localhost:6006/?path=/docs/components-seasons-seasonsactions--docs)**: Provides edit, export, and delete actions for each season within the table.
        \n- **[SummaryComponent](http://localhost:6006/?path=/docs/components-seasons-summarycomponent--docs)**: Displays key statistics and metrics related to a season, such as visits completed and total workers reached.\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    isManager: {
      control: { type: 'boolean' },
      description: 'Indicates whether the user has manager access to the page.',
      table: {
        type: { summary: 'boolean' },
        required: true,
      },
    },
    organization: {
      control: { type: 'object' },
      description: 'The organization data containing the ID and other related fields.',
      table: {
        type: { summary: 'object' },
        required: true,
      },
    },
  },
};

const Template = () => null;

export const SeasonsDocsOnly = Template.bind({});
SeasonsDocsOnly.parameters = generateStoryDocs(
  // When is this variant used?
  `\nThe **Seasons** page is used whenever managers, planners, or users need to structure their outreach data into custom timeframes known as seasons. By defining seasons with specific names and date ranges, this page allows for better organization of outreach activities, enabling teams to track efforts within relevant time periods and align their planning with seasonal goals.
      \n\n**Includes:**
      \n- Breadcrumbs for navigation.
      \n- Table for displaying seasons with sortable columns.
      \n- CRUD operations (create, edit, delete) for managing seasons.
      \n- Export functionality for season data.
      \n- Pagination for navigating through season records.
      \n- Empty state display when no seasons are available.\n\n`,
  // Where is it currently used on the product?
  'This page can be accessed through the `Dashboard menu` --> `Admin` --> `Seasons`.',
);
